import { Typography } from '@mui/material';
import { getLocalDate } from '@surfline/web-common';
import { ScaleTime } from 'd3-scale';
import React, { useEffect, useMemo } from 'react';
import { ConditionRating } from '../../types/conditionRating';
import { LiveWindPoint } from '../../types/liveWind';
import styles from './LiveWindBarGraph.module.scss';
import useSetupBarGraphCanvas from './useSetupBarGraphCanvas';

interface Props {
  liveWind: Array<LiveWindPoint>;
  xScale: ScaleTime<number, number>;
  ratings: Array<ConditionRating>;
  canvasRef: React.MutableRefObject<HTMLDivElement>;
  currentMarkerXPercentage: number;
}

interface RatingsByTimestamp {
  [timestamp: number]: ConditionRating;
}

const LiveWindBarGraph: React.FC<Props> = ({
  liveWind,
  xScale,
  ratings,
  canvasRef,
  currentMarkerXPercentage,
}) => {
  // create memoized lookup of condition ratings by hour
  const ratingsByTimestamp: RatingsByTimestamp = useMemo(
    () =>
      ratings.reduce((next, rating) => {
        const { timestamp, utcOffset } = rating;
        return { ...next, [getLocalDate(timestamp, utcOffset).getHours()]: rating };
      }, {}),
    [ratings],
  );

  // attach condition rating to each live wind reading
  const liveWindWithRatings = useMemo(
    () =>
      liveWind.map((reading) => ({
        ...reading,
        condition:
          ratingsByTimestamp[getLocalDate(reading.timestamp, reading.utcOffset).getHours()],
      })),
    [liveWind, ratingsByTimestamp],
  );

  const { setUpBarGraph } = useSetupBarGraphCanvas(
    liveWindWithRatings,
    xScale,
    currentMarkerXPercentage,
    canvasRef,
  );

  useEffect(setUpBarGraph, [setUpBarGraph]);

  return (
    <div className={styles.onshoreOffshoreGraphContainer} data-testid="live-wind-bar-graph">
      <Typography variant="caption2" color="textSecondary" position="absolute" margin="5px">
        Offshore
      </Typography>
      <Typography
        variant="caption2"
        color="text.secondary"
        position="absolute"
        margin="5px"
        bottom={0}
      >
        Onshore
      </Typography>
      <div id="onshore-offshore-graph-canvas" className={styles.onshoreOffshoreGraphCanvas} />
    </div>
  );
};

export default LiveWindBarGraph;
